import cn from 'classnames';
import React, { useMemo } from 'react';

import { useDispatch, useSelector } from 'editor/src/store/hooks';
import resetPageCountOperation from 'editor/src/store/variants/operation/resetPageCountOperation';
import { PageOptions } from 'editor/src/store/variants/types';

import PropertySeparator from 'editor/src/component/DesktopSidebar/TabContents/PropertiesTabContent/PropertySeparator';
import Select, { SelectOption } from 'editor/src/component/Select';

import generatePageOptions from './generatePageOptions';
import isCustomPageCountProduct from './isCustomPageCountProduct';
import PageCountInput from './PageCountInput';

import styles from './index.module.scss';
import controlStyles from '../ProductControl.module.scss';

interface Props {
  selectedPageCount: number;
  pages: PageOptions;
}

function PageCountControl({ pages, selectedPageCount }: Props) {
  const dispatch = useDispatch();
  const selectedVariantsCount = useSelector((state) => state.variants.variationGroups.length);
  const isSingleSelection = useSelector((state) => state.variants.configuration.singleSelection);
  const productUid = useSelector((state) => state.design.designData?.product_uid) || '';
  const dimensions = useSelector((state) => state.design.designData?.dimensions);
  const productVariations = useSelector((state) => state.variants.product?.productVariations);
  const options = useMemo(() => generatePageOptions(pages), [pages]);
  const isCustomPageCount = useMemo(
    () => isCustomPageCountProduct(productVariations || [], productUid),
    [productVariations, productUid],
  );
  // With several variants selected a page-count change would have to switch all of them;
  // a single selected variant switches cleanly through design.switchToProduct.
  const disabled = !isSingleSelection && selectedVariantsCount > 1;

  const applyPageCount = (pageCount: number) => {
    dispatch(resetPageCountOperation(pageCount, dimensions, productUid));
  };

  const onSelect = (option: SelectOption) => {
    const selectedOption = options.find(({ value }) => value === option.value);
    if (selectedOption) {
      applyPageCount(selectedOption.value);
    }
  };

  const selectedOption = options.find((option) => option.value === selectedPageCount);

  return (
    <>
      <PropertySeparator bigMargin />
      <div className={controlStyles.controlTitle}>{pages.name}</div>
      {isCustomPageCount ? (
        <PageCountInput
          pages={pages}
          selectedPageCount={selectedPageCount}
          disabled={disabled}
          onChange={applyPageCount}
        />
      ) : (
        <Select
          options={options}
          onSelect={onSelect}
          selected={[selectedOption?.value || '']}
          className={cn('cy-page-count-select', {
            [styles.disabled]: disabled,
          })}
        />
      )}
    </>
  );
}

export default React.memo(PageCountControl);
